import React, { useEffect, useState } from 'react';
import {
  FiHome,
  FiUsers,
  FiUserPlus,
  FiEdit3,
  FiCheck,
  FiX,
  FiEye,
  FiClock,
  FiMoreVertical
} from 'react-icons/fi';
import { useAdminAuth } from '../../../hooks/admin/useAdminAuth';
import { API_URL } from '../../../services/api';

interface Activity {
  id: string;
  type: 'company_added' | 'company_updated' | 'user_registered' | 'request_approved' | 'request_rejected' | 'company_viewed';
  title: string;
  description: string;
  timestamp: string;
  user: {
    name: string;
    avatar?: string;
  };
  metadata?: {
    companyName?: string;
    userName?: string;
    changes?: string[];
    viewCount?: number;
  };
}

const RecentActivities: React.FC = () => {
  const { token } = useAdminAuth();
  const [activities, setActivities] = useState<Activity[]>([]);
  const [loading, setLoading] = useState(true);

  // Fetch recent activities from API
  useEffect(() => {
    const fetchActivities = async () => {
      if (!token) return;

      try {
        
        const response = await fetch(`${API_URL}/api/admin/dashboard/recent-activities`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });

        if (response.ok) {
          const data = await response.json();

          // Transform API data to component format
          const transformedActivities = data.map((activity: any) => ({
            id: activity.activity_id,
            type: mapActivityType(activity.activity_type),
            title: getActivityTitle(activity.activity_type),
            description: activity.description,
            timestamp: formatTimestamp(activity.created_at),
            user: {
              name: activity.user_email || 'System'
            },
            metadata: extractMetadata(activity.description)
          }));

          setActivities(transformedActivities);
        } else {
          console.error('API error:', response.status);
          setActivities([]);
        }
      } catch (error) {
        console.error('Failed to fetch activities:', error);
        setActivities([]);
      } finally {
        setLoading(false);
      }
    };

    fetchActivities();
  }, [token]);

  // Helper function to format timestamp
  const formatTimestamp = (timestamp: string): string => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 60) {
      return `${diffMins} นาทีที่แล้ว`;
    } else if (diffHours < 24) {
      return `${diffHours} ชั่วโมงที่แล้ว`;
    } else {
      return `${diffDays} วันที่แล้ว`;
    }
  };

  // Map backend activity types to frontend types
  const mapActivityType = (backendType: string): Activity['type'] => {
    const typeMapping: Record<string, Activity['type']> = {
      'company_created': 'company_added',
      'company_updated': 'company_updated',
      'user_registered': 'user_registered',
      'claim_approved': 'request_approved',
      'claim_rejected': 'request_rejected',
      'company_viewed': 'company_viewed'
    };
    return typeMapping[backendType] || 'company_viewed';
  };

  // Generate title from activity type
  const getActivityTitle = (activityType: string): string => {
    const titleMapping: Record<string, string> = {
      'company_created': 'เพิ่มบริษัทใหม่',
      'company_updated': 'อัพเดทข้อมูลบริษัท',
      'user_registered': 'ผู้ใช้งานใหม่',
      'claim_approved': 'อนุมัติคำขอแก้ไข',
      'claim_rejected': 'ปฏิเสธคำขอ',
      'company_viewed': 'บริษัทได้รับความสนใจ'
    };
    return titleMapping[activityType] || 'กิจกรรมอื่นๆ';
  };

  // Extract metadata from description (if needed)
  const extractMetadata = (description: string): Activity['metadata'] => {
    // Extract company name from description (simple regex match)
    const companyMatch = description.match(/บริษัท (.+?)(?:\s|$)/);
    return {
      companyName: companyMatch ? companyMatch[1] : undefined
    };
  };

  // If still loading, show skeleton
  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">กิจกรรมล่าสุด</h3>
              <p className="text-sm text-gray-600 mt-1">
                กำลังโหลดข้อมูล...
              </p>
            </div>
          </div>
        </div>
        <div className="p-6">
          <div className="animate-pulse space-y-4">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div key={i} className="flex space-x-3">
                <div className="h-8 w-8 bg-gray-200 rounded-full"></div>
                <div className="flex-1 space-y-2">
                  <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                  <div className="h-3 bg-gray-200 rounded w-1/2"></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  const getActivityIcon = (type: Activity['type']) => {
    switch (type) {
      case 'company_added':
        return <FiHome className="h-5 w-5 text-blue-600" />;
      case 'company_updated':
        return <FiEdit3 className="h-5 w-5 text-yellow-600" />;
      case 'user_registered':
        return <FiUserPlus className="h-5 w-5 text-green-600" />;
      case 'request_approved':
        return <FiCheck className="h-5 w-5 text-green-600" />;
      case 'request_rejected':
        return <FiX className="h-5 w-5 text-red-600" />;
      case 'company_viewed':
        return <FiEye className="h-5 w-5 text-purple-600" />;
      default:
        return <FiClock className="h-5 w-5 text-gray-600" />;
    }
  };

  const getActivityColor = (type: Activity['type']) => {
    switch (type) {
      case 'company_added':
        return 'bg-blue-100';
      case 'company_updated':
        return 'bg-yellow-100';
      case 'user_registered':
        return 'bg-green-100';
      case 'request_approved':
        return 'bg-green-100';
      case 'request_rejected':
        return 'bg-red-100';
      case 'company_viewed':
        return 'bg-purple-100';
      default:
        return 'bg-gray-100';
    }
  };

  const renderActivityMetadata = (activity: Activity) => {
    if (!activity.metadata) return null;

    return (
      <div className="mt-2 text-xs text-gray-500">
        {activity.metadata.companyName && (
          <span className="inline-flex items-center px-2 py-1 rounded-full bg-gray-100 text-gray-700 mr-2">
            <FiHome className="h-3 w-3 mr-1" />
            {activity.metadata.companyName}
          </span>
        )}
        {activity.metadata.userName && (
          <span className="inline-flex items-center px-2 py-1 rounded-full bg-blue-100 text-blue-700 mr-2">
            <FiUsers className="h-3 w-3 mr-1" />
            {activity.metadata.userName}
          </span>
        )}
        {activity.metadata.viewCount && (
          <span className="inline-flex items-center px-2 py-1 rounded-full bg-purple-100 text-purple-700 mr-2">
            <FiEye className="h-3 w-3 mr-1" />
            {activity.metadata.viewCount} ครั้ง
          </span>
        )}
        {activity.metadata.changes && activity.metadata.changes.length > 0 && (
          <div className="mt-1">
            <span className="text-gray-600">เปลี่ยนแปลง: </span>
            {activity.metadata.changes.map((change, index) => (
              <span
                key={index}
                className="inline-flex items-center px-2 py-1 rounded-full bg-yellow-100 text-yellow-700 text-xs mr-1"
              >
                {change}
              </span>
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200">
      <div className="p-6 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">กิจกรรมล่าสุด</h3>
            <p className="text-sm text-gray-600 mt-1">
              ติดตามการเปลี่ยนแปลงและกิจกรรมในระบบ
            </p>
          </div>
          <button className="p-2 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100">
            <FiMoreVertical className="h-5 w-5" />
          </button>
        </div>
      </div>

      <div className="p-6">
        {activities.length === 0 ? (
          <div className="text-center py-12">
            <FiClock className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500 text-sm">ยังไม่มีกิจกรรมในระบบ</p>
          </div>
        ) : (
          <div className="flow-root">
            <ul className="-mb-8">
              {activities.map((activity, index) => (
                <li key={activity.id}>
                  <div className="relative pb-8">
                    {index !== activities.length - 1 && (
                      <span
                        className="absolute top-4 left-4 -ml-px h-full w-0.5 bg-gray-200"
                        aria-hidden="true"
                      />
                    )}
                    <div className="relative flex space-x-3">
                      <div className={`flex h-8 w-8 items-center justify-center rounded-full ring-8 ring-white ${getActivityColor(activity.type)}`}>
                        {getActivityIcon(activity.type)}
                      </div>
                      <div className="flex min-w-0 flex-1 justify-between space-x-4">
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-medium text-gray-900">
                            {activity.title}
                          </p>
                          <p className="text-sm text-gray-600 mt-1">
                            {activity.description}
                          </p>
                          <p className="text-xs text-gray-500 mt-1">
                            โดย {activity.user.name}
                          </p>
                          {renderActivityMetadata(activity)}
                        </div>
                        <div className="whitespace-nowrap text-right text-sm text-gray-500">
                          <time>{activity.timestamp}</time>
                        </div>
                      </div>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      <div className="px-6 py-3 bg-gray-50 border-t border-gray-200 rounded-b-lg">
        <div className="text-sm">
          <a href="/admin/activities" className="font-medium text-blue-600 hover:text-blue-500">
            ดูกิจกรรมทั้งหมด
          </a>
        </div>
      </div>
    </div>
  );
};

export default RecentActivities;